import cn from 'classnames';
import { FC, KeyboardEvent, useCallback } from 'react';
import { useIntl } from 'react-intl';

import { RegistryAsset } from '@/shared/api/transcriptor/registryApi';
import { getLanguageDisplayName } from '@/shared/lib';

import styles from './RegistryAssetSelectorCard.module.scss';

type RegistryAssetSelectorCardProps = {
  asset: RegistryAsset;
  className?: string;
  disabled?: boolean;
  onClick: (asset: RegistryAsset) => void;
};

export const RegistryAssetSelectorCard: FC<RegistryAssetSelectorCardProps> = (
  props,
) => {
  const { asset, className, disabled = false, onClick } = props;
  const { formatMessage, locale } = useIntl();

  const handleClick = useCallback(() => {
    if (disabled) {
      return;
    }

    onClick(asset);
  }, [asset, disabled, onClick]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (disabled) {
        return;
      }

      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onClick(asset);
      }
    },
    [asset, disabled, onClick],
  );

  const languageLabel = getLanguageDisplayName(asset.language, locale);

  const description =
    asset.description ??
    formatMessage({
      id: 'communityCatalog.cardNoDescription',
      defaultMessage: 'No description available',
    });

  const typeLabel = asset.templateType
    ? asset.templateType.replaceAll('_', ' ')
    : formatMessage({
        id: 'global.document',
        defaultMessage: 'Document',
      });

  return (
    <div
      data-component="registry-asset-selector-card"
      className={cn(styles.container, disabled && styles.disabled, className)}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
    >
      <div className={styles.badges}>
        <span
          className={cn(
            'p4',
            styles.badge,
            asset.originKind === 'official'
              ? styles.badgeOfficial
              : styles.badgeCommunity,
          )}
        >
          {asset.originKind === 'official'
            ? formatMessage({
                id: 'communityCatalog.cardBadgeOfficial',
                defaultMessage: 'Official',
              })
            : formatMessage({
                id: 'communityCatalog.cardBadgeCommunity',
                defaultMessage: 'Community',
              })}
        </span>
        {languageLabel && (
          <span className={cn('p4', styles.badge, styles.badgeLanguage)}>
            {languageLabel}
          </span>
        )}
        {asset.hasLocalClone && (
          <span className={cn('p4', styles.badge, styles.badgeImported)}>
            {formatMessage({
              id: 'communityCatalog.cardBadgeAlreadyImported',
              defaultMessage: 'Added',
            })}
          </span>
        )}
      </div>

      <h3 className={cn('h5', styles.name)}>{asset.name}</h3>

      <p className={cn('p3', styles.description)}>{description}</p>

      <div className={styles.meta}>
        <span className={cn('p4', styles.metaChip)}>{typeLabel}</span>
      </div>
    </div>
  );
};
